import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ApiContext } from '@shared/contexts';
import { toApiException } from '@shared/helpers/toApiException';
import { useAbortableAttempt, usePollingJob, useWatchedRun } from '@shared/hooks';
import { EBulkOperationStatus } from '@modules/items/types/bulkOperation.types';

import isAiBudgetRefusal from '../helpers/isAiBudgetRefusal.helper';
import { GROUNDED_QA_POLL_INTERVAL_MS } from './constants';
import {
  EGroundedQaFeedbackSentiment,
  TGroundedQaCitationLocatorResponse,
  TGroundedQaFeedbackInput,
  TGroundedQaRequest,
  TGroundedQaRunResponse,
} from './types';

type TFeedbackMutationArgs = { runId: string; feedback: TGroundedQaFeedbackInput };

type TArgs = {
  setResponse: (response: TGroundedQaRunResponse | null) => void;
  pendingRunId: string | null;
  setPendingRunId: (runId: string | null) => void;
};

export const useGroundedQa = ({ setResponse, pendingRunId, setPendingRunId }: TArgs) => {
  const { apiClient, handleApiError, queryClient } = useContext(ApiContext);
  const { watchedRunIdRef, deliver, watch, unwatch } = useWatchedRun({ pendingRunId, setPendingRunId, setResponse });
  const attempt = useAbortableAttempt();
  const attemptSignalRef = useRef<AbortSignal | null>(null);
  const [error, setError] = useState<unknown>(null);
  const polling = usePollingJob<TGroundedQaRunResponse>({
    queryKeyPrefix: 'grounded-qa-run',
    endpoint: '/ai/grounded-qa',
    pollingInterval: GROUNDED_QA_POLL_INTERVAL_MS,
    showToast: false,
    onCompleted: deliver,
    onFailed: deliver,
  });
  // A dead poll (retries exhausted) can no longer deliver; keeping the watch would leave a
  // stale pending id behind.
  useEffect(() => {
    if (!polling.isError) return;
    unwatch();
    setError(polling.error);
  }, [polling.isError]);

  const cancelRun = useMutation<void, Error, string>({
    mutationFn: async (runId) => {
      await apiClient.put(`/ai/grounded-qa/${runId}/cancel-run`);
    },
  });

  const mutation = useMutation<TGroundedQaRunResponse, Error, TGroundedQaRequest>({
    mutationFn: async (input) => {
      const res = await apiClient.post<TGroundedQaRunResponse>('/ai/grounded-qa', input);
      return res.data;
    },
    onSuccess: (data) => {
      // Cancelled while the create request was in flight: stop the run now that its id is known.
      if (attemptSignalRef.current?.aborted) {
        if (data.status === EBulkOperationStatus.CREATED) cancelRun.mutate(data.runId);
        return;
      }

      if (data.status === EBulkOperationStatus.CREATED) {
        queryClient.setQueryData([`grounded-qa-run-${data.runId}`], () => data);
        polling.setJobId(data.runId);
        watch(data.runId);
        return;
      }

      setResponse(data);
    },
    onError: (mutationError, variables, context) => {
      if (isAiBudgetRefusal(toApiException(mutationError))) return;
      handleApiError('Failed to get an answer!', false)(mutationError, variables, context);
      if (!attemptSignalRef.current?.aborted) setError(mutationError);
    },
  });

  const submit = (input: TGroundedQaRequest) => {
    attemptSignalRef.current = attempt.begin();
    setError(null);
    setResponse(null);
    unwatch();
    polling.setJobId(null);
    mutation.mutate(input);
  };

  const isPending = mutation.isPending || polling.isPolling;

  // Cancelling needs a run id to address, so it is only offered once the create response
  // delivered one (canCancel); during the create round-trip the button is disabled.
  const cancel = () => {
    attempt.abort();
    setError(null);
    if (watchedRunIdRef.current) cancelRun.mutate(watchedRunIdRef.current);
    polling.setJobId(null);
    unwatch();
    setResponse(null);
  };

  const reset = () => {
    cancel();
    mutation.reset();
  };

  const pendingRun = isPending ? (polling.data ?? null) : null;
  const apiError = toApiException(mutation.error);
  const budgetRefusal = isAiBudgetRefusal(apiError) ? apiError : null;

  return { submit, reset, cancel, canCancel: Boolean(pendingRunId), isPending, pendingRun, budgetRefusal, error };
};

export const useGroundedQaCitationLocator = (runId: string | null, index: string | null) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const query = useQuery<TGroundedQaCitationLocatorResponse>({
    retry,
    queryKey: ['grounded-qa-citation-locator', runId, index],
    queryFn: async () => {
      const res = await apiClient.get<TGroundedQaCitationLocatorResponse>(`/ai/grounded-qa/${runId}/citation-locator`, {
        params: { index },
      });
      return res.data;
    },
    enabled: Boolean(runId) && index !== null,
    staleTime: Infinity,
  });
  useHandleQueryError(
    query.isError,
    query.error,
    'Loading the citation failed!',
    'Could not load the citation. Please try again.'
  );

  return query;
};

export const useGroundedQaFeedback = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);

  return useMutation<void, Error, TFeedbackMutationArgs>({
    mutationFn: async ({ runId, feedback }) => {
      await apiClient.put(`/ai/grounded-qa/${runId}/feedback`, feedback);
    },
    onSuccess: (_, { feedback }) => {
      const message =
        feedback.sentiment === EGroundedQaFeedbackSentiment.POSITIVE ? 'Positive feedback sent.' : 'Feedback sent.';
      show('success', message);
    },
    onError: handleApiError('Sending feedback failed!'),
  });
};
